import { supabase } from '@/lib/supabase';
import type { Organization, OrgListItem, OrgWithRelations, AdminStats, OrgSubscription } from '@/types';

export const orgsApi = {
  list: async (params: {
    search?: string;
    status?: string;
    page?: number;
    perPage?: number;
  } = {}): Promise<{ items: OrgListItem[]; total: number }> => {
    const { page = 1, perPage = 50, search, status } = params;
    const from = (page - 1) * perPage;
    const to = from + perPage - 1;

    let query = supabase
      .from('organizations')
      .select(
        '*, product_subscriptions(product_code, status, plan), org_users(id), org_system_subscriptions(system_code, plan_code, status, billing_cycle)',
        { count: 'exact' }
      )
      .order('created_at', { ascending: false })
      .range(from, to);

    if (search) query = query.ilike('name', `%${search}%`);
    if (status) query = query.eq('status', status);

    const { data, count, error } = await query;
    if (error) throw error;

    return { items: data as OrgListItem[], total: count || 0 };
  },

  get: async (orgId: string): Promise<OrgWithRelations> => {
    const [orgResult, intResult] = await Promise.all([
      supabase.from('organizations').select('*').eq('id', orgId).single(),
      supabase.from('org_integrations').select('*').eq('organization_id', orgId),
    ]);

    if (orgResult.error) throw orgResult.error;

    return {
      ...orgResult.data,
      subscriptions: [], // fetched separately via licensingApi.getOrgSubscriptions
      integrations: intResult.data || [],
    } as OrgWithRelations;
  },

  create: async (data: Partial<Organization>): Promise<Organization> => {
    const { data: org, error } = await supabase
      .from('organizations')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return org as Organization;
  },

  update: async (orgId: string, data: Partial<Organization>): Promise<Organization> => {
    const { data: org, error } = await supabase
      .from('organizations')
      .update(data)
      .eq('id', orgId)
      .select()
      .single();

    if (error) throw error;
    return org as Organization;
  },

  /**
   * Soft-archive: flip status to 'inactive' and stamp archived_at.
   * Reversible via unarchive(). No data loss.
   */
  archive: async (orgId: string): Promise<void> => {
    const { error } = await supabase
      .from('organizations')
      .update({ status: 'inactive', archived_at: new Date().toISOString() })
      .eq('id', orgId);
    if (error) throw error;
  },

  /** Restore an archived org: clear archived_at and flip status back to 'active'. */
  unarchive: async (orgId: string): Promise<void> => {
    const { error } = await supabase
      .from('organizations')
      .update({ status: 'active', archived_at: null })
      .eq('id', orgId);
    if (error) throw error;
  },

  /**
   * Hard-delete. Only allowed when the org has no org_users and no active
   * subscriptions (org_subscriptions + org_system_subscriptions).
   * UI must confirm before calling.
   */
  hardDelete: async (orgId: string): Promise<void> => {
    const [usersRes, subsRes, sysSubsRes] = await Promise.all([
      supabase.from('org_users').select('id', { count: 'exact', head: true }).eq('org_id', orgId),
      supabase.from('org_subscriptions').select('id', { count: 'exact', head: true }).eq('organization_id', orgId),
      supabase.from('org_system_subscriptions').select('id', { count: 'exact', head: true }).eq('organization_id', orgId),
    ]);
    const hasUsers = (usersRes.count ?? 0) > 0;
    const hasSubs = (subsRes.count ?? 0) > 0 || (sysSubsRes.count ?? 0) > 0;
    if (hasUsers || hasSubs) {
      const parts = [hasUsers ? 'users' : null, hasSubs ? 'subscriptions' : null].filter(Boolean).join(' and ');
      throw new Error(`Cannot delete: org still has ${parts}. Archive instead.`);
    }
    const { error } = await supabase.from('organizations').delete().eq('id', orgId);
    if (error) throw error;
  },

  getStats: async (): Promise<AdminStats> => {
    const [orgsResult, subsResult, waResult, errResult] = await Promise.all([
      supabase.from('organizations').select('id, status', { count: 'exact' }),
      supabase.from('product_subscriptions').select('id').eq('status', 'active'),
      supabase.from('org_whatsapp_accounts').select('id', { count: 'exact' }).eq('status', 'active'),
      supabase.from('org_whatsapp_accounts').select('id', { count: 'exact' }).eq('status', 'disconnected'),
    ]);

    const orgs = orgsResult.data || [];
    const activeOrgs = orgs.filter((o: { status: string }) => o.status === 'active').length;

    return {
      total_orgs: orgsResult.count || 0,
      active_orgs: activeOrgs,
      active_subs: subsResult.data?.length || 0,
      greenapi_instances: waResult.count || 0,
      whatsapp_accounts: waResult.count || 0,
      integration_errors: errResult.count || 0,
    };
  },

  setupPortalDomain: async (
    orgId: string,
    slug: string
  ): Promise<{ success: boolean; domain?: string; error?: string }> => {
    const { data, error } = await supabase.functions.invoke('setup-org-portal', {
      body: { org_id: orgId, slug },
    });
    if (error) throw error;
    return data as { success: boolean; domain?: string; error?: string };
  },

  /** Cloudflare R2 storage billing (admin override for used GB until R2 sync exists). */
  updateStorageBilling: async (
    orgId: string,
    params: { pricePerGbAgorot: number; freeGb: number; usedGb: number }
  ): Promise<void> => {
    const { error } = await supabase
      .from('organizations')
      .update({
        storage_price_per_gb_agorot: params.pricePerGbAgorot,
        storage_free_gb: params.freeGb,
        storage_used_gb: params.usedGb,
      })
      .eq('id', orgId);
    if (error) throw error;
  },

  /**
   * Re-send the magic-link owner invite via email or WhatsApp.
   * Calls the 'resend-owner-invite' Edge Function.
   */
  resendOwnerInvite: async (
    orgId: string,
    channel: 'email' | 'whatsapp'
  ): Promise<{ sent_to: string }> => {
    const { data, error } = await supabase.functions.invoke('resend-owner-invite', {
      body: { org_id: orgId, channel },
    });
    if (error) throw error;
    return data as { sent_to: string };
  },
};

export async function getOrgSubscription(orgId: string): Promise<OrgSubscription | null> {
  const { data, error } = await supabase
    .from('org_subscriptions')
    .select('*, subscription_plans(name, plan_code)')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as OrgSubscription | null;
}

// ─── Affiliate / Ambassador ──────────────────────────────────────────────────

export interface AffiliateReferral {
  id: string;
  referrer_org_id: string;
  referred_org_id: string;
  status: 'pending' | 'active' | 'expired' | 'cancelled';
  commission_pct: number;
  months_remaining: number;
  total_earned_agorot: number;
  activated_at: string | null;
  expires_at: string | null;
  created_at: string;
  referred_org?: { name: string; slug: string; status: string };
}

export interface AffiliateCreditTx {
  id: string;
  org_id: string;
  referral_id: string | null;
  amount_agorot: number;
  type: 'earn' | 'apply' | 'expire' | 'manual';
  description: string | null;
  created_at: string;
}

export const affiliateApi = {
  /** All referrals this org created (orgs it referred) */
  getReferrals: async (orgId: string): Promise<AffiliateReferral[]> => {
    const { data: rows, error } = await supabase
      .from('affiliate_referrals')
      .select('*')
      .eq('referrer_org_id', orgId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    if (!rows?.length) return [];

    const ids = [...new Set(rows.map((r: { referred_org_id: string }) => r.referred_org_id).filter(Boolean))];
    const { data: orgs, error: orgErr } = await supabase
      .from('organizations')
      .select('id, name, slug, status')
      .in('id', ids);
    if (orgErr) throw orgErr;
    const orgById = new Map((orgs ?? []).map((o: { id: string; name: string; slug: string; status: string }) => [o.id, o]));

    return rows.map((r: AffiliateReferral & { referred_org_id: string }) => ({
      ...r,
      referred_org: orgById.get(r.referred_org_id),
    }));
  },

  /** Recent credit transactions for this org */
  getCreditHistory: async (orgId: string): Promise<AffiliateCreditTx[]> => {
    const { data, error } = await supabase
      .from('affiliate_credit_transactions')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error) throw error;
    return (data ?? []) as AffiliateCreditTx[];
  },

  /**
   * Manually credit this month's commission for one referral.
   * amount_agorot: the 20% commission amount for this month (entered by Nizan).
   */
  processMonthlyCredit: async (
    referralId: string,
    referrerOrgId: string,
    amountAgorot: number,
    note: string,
  ): Promise<void> => {
    const { error: txError } = await supabase.from('affiliate_credit_transactions').insert({
      org_id: referrerOrgId,
      referral_id: referralId,
      amount_agorot: amountAgorot,
      type: 'earn',
      description: note,
    });
    if (txError) throw txError;

    const { data: org, error: orgFetchErr } = await supabase
      .from('organizations')
      .select('affiliate_credits_agorot')
      .eq('id', referrerOrgId)
      .single();
    if (orgFetchErr) throw orgFetchErr;

    const nextBalance = (org?.affiliate_credits_agorot ?? 0) + amountAgorot;
    const { error: updateOrgErr } = await supabase
      .from('organizations')
      .update({ affiliate_credits_agorot: nextBalance })
      .eq('id', referrerOrgId);
    if (updateOrgErr) throw updateOrgErr;

    const { data: ref, error: refFetchErr } = await supabase
      .from('affiliate_referrals')
      .select('months_remaining, total_earned_agorot')
      .eq('id', referralId)
      .single();
    if (refFetchErr) {
      console.warn('Failed to load referral for counter update:', refFetchErr);
      return;
    }
    const { error: refError } = await supabase
      .from('affiliate_referrals')
      .update({
        months_remaining: Math.max(0, Number(ref?.months_remaining ?? 0) - 1),
        total_earned_agorot: Number(ref?.total_earned_agorot ?? 0) + amountAgorot,
      })
      .eq('id', referralId);
    if (refError) console.warn('Failed to update referral counters:', refError);
  },
};
